/**
 * SVG Icons for Game UI
 * Hand-drawn style icons matching the medieval Chinese aesthetic
 */

/**
 * Creates an SVG element from string
 */
export function createSVGElement(svgString: string): string {
  return svgString.trim();
}

/**
 * Resource Icons
 */
export const ICONS = {
  // Gold coin stack
  gold: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="17" rx="8" ry="3" fill="#b8860b"/>
    <ellipse cx="12" cy="16" rx="8" ry="3" fill="#d4a017"/>
    <ellipse cx="12" cy="14" rx="8" ry="3" fill="#b8860b"/>
    <ellipse cx="12" cy="13" rx="8" ry="3" fill="#d4a017"/>
    <ellipse cx="12" cy="11" rx="8" ry="3" fill="#b8860b"/>
    <ellipse cx="12" cy="10" rx="8" ry="3" fill="#e8c252"/>
    <ellipse cx="12" cy="10" rx="6" ry="2" fill="#f5d547"/>
    <ellipse cx="12" cy="10" rx="3" ry="1" fill="#ffeaa7" opacity="0.6"/>
  </svg>`,

  // Crossed swords (units/military)
  swords: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L18 18M4 4L6 6M4 4L4 7M4 4L7 4" stroke="#b0b0b0" stroke-width="2" stroke-linecap="round"/>
    <path d="M20 4L6 18M20 4L18 6M20 4L20 7M20 4L17 4" stroke="#b0b0b0" stroke-width="2" stroke-linecap="round"/>
    <path d="M5 19L7 17M19 19L17 17" stroke="#8b4513" stroke-width="2.5" stroke-linecap="round"/>
    <circle cx="6" cy="18" r="1.5" fill="#8b4513"/>
    <circle cx="18" cy="18" r="1.5" fill="#8b4513"/>
  </svg>`,

  // Castle/territory
  territory: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="12" width="18" height="10" fill="#6b4423" rx="1"/>
    <rect x="6" y="8" width="4" height="4" fill="#8b5a2b"/>
    <rect x="14" y="8" width="4" height="4" fill="#8b5a2b"/>
    <rect x="9" y="4" width="6" height="8" fill="#a0522d"/>
    <rect x="3" y="10" width="2" height="4" fill="#5c4033"/>
    <rect x="19" y="10" width="2" height="4" fill="#5c4033"/>
    <rect x="10" y="15" width="4" height="7" fill="#3d2617"/>
    <path d="M7 8L8 5L9 8" fill="#c62828"/>
    <path d="M15 8L16 5L17 8" fill="#c62828"/>
  </svg>`,

  // Time/hourglass
  time: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 2H17V6L12 12L7 6V2Z" fill="#d4af37" stroke="#b8860b" stroke-width="1"/>
    <path d="M7 22H17V18L12 12L7 18V22Z" fill="#d4af37" stroke="#b8860b" stroke-width="1"/>
    <rect x="6" y="2" width="12" height="2" fill="#8b6914" rx="0.5"/>
    <rect x="6" y="20" width="12" height="2" fill="#8b6914" rx="0.5"/>
    <path d="M9 4V5.5L12 9L15 5.5V4" fill="#ffeaa7"/>
    <path d="M10 20V18L12 15L14 18V20" fill="#e8c252"/>
  </svg>`,

  // Shield (defense)
  shield: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L4 6V12C4 17 12 22 12 22C12 22 20 17 20 12V6L12 2Z" fill="#8b4513" stroke="#6b3410" stroke-width="1"/>
    <path d="M12 4L6 7V12C6 15.5 12 19 12 19C12 19 18 15.5 18 12V7L12 4Z" fill="#a0522d"/>
    <circle cx="12" cy="11" r="4" fill="#d4af37" stroke="#b8860b" stroke-width="1"/>
    <circle cx="12" cy="11" r="2" fill="#f5d547"/>
  </svg>`,

  // Bow and arrow (archer)
  bow: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 3C6 3 3 8 3 12C3 16 6 21 6 21" stroke="#8b4513" stroke-width="2.5" stroke-linecap="round" fill="none"/>
    <line x1="6" y1="3" x2="6" y2="21" stroke="#c4a77d" stroke-width="1"/>
    <line x1="6" y1="12" x2="21" y2="12" stroke="#5d4037" stroke-width="1.5"/>
    <path d="M19 12L21 10.5V13.5L19 12Z" fill="#607d8b"/>
    <path d="M16 9L18 12L16 15" fill="none" stroke="#8b4513" stroke-width="1"/>
  </svg>`,

  // Spear
  spear: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="4" x2="12" y2="22" stroke="#8b4513" stroke-width="2"/>
    <path d="M12 2L8 8H16L12 2Z" fill="#b0b0b0" stroke="#808080" stroke-width="0.5"/>
    <ellipse cx="12" cy="8" rx="2" ry="0.5" fill="#c62828"/>
  </svg>`,

  // Horse (cavalry)
  horse: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 18L4 22H6L8 18" stroke="#5d4037" stroke-width="2" stroke-linecap="round"/>
    <path d="M16 18L14 22H16L18 18" stroke="#5d4037" stroke-width="2" stroke-linecap="round"/>
    <ellipse cx="12" cy="14" rx="7" ry="5" fill="#8b4513"/>
    <ellipse cx="18" cy="10" rx="3" ry="4" fill="#8b4513"/>
    <path d="M20 6C20 6 22 4 21 3C20 2 18 4 18 6" stroke="#5d4037" stroke-width="1.5" fill="none"/>
    <circle cx="19" cy="9" r="1" fill="#1a1a1a"/>
  </svg>`,

  // Magic staff (mage)
  magic: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="12" y1="8" x2="12" y2="22" stroke="#6b4423" stroke-width="2.5"/>
    <circle cx="12" cy="5" r="4" fill="#4fc3f7" stroke="#29b6f6" stroke-width="1"/>
    <circle cx="12" cy="5" r="2" fill="#81d4fa"/>
    <circle cx="12" cy="5" r="1" fill="#e1f5fe"/>
    <circle cx="8" cy="3" r="1" fill="#4fc3f7" opacity="0.6"/>
    <circle cx="16" cy="4" r="0.8" fill="#4fc3f7" opacity="0.6"/>
    <circle cx="14" cy="2" r="0.6" fill="#4fc3f7" opacity="0.6"/>
  </svg>`,

  // Pause
  pause: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/>
    <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/>
  </svg>`,

  // Play
  play: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4L20 12L6 20V4Z" fill="currentColor"/>
  </svg>`,

  // Settings gear
  settings: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
    <path d="M12 2V4M12 20V22M2 12H4M20 12H22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  // Zoom in
  zoomIn: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
    <line x1="15" y1="15" x2="21" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <line x1="10" y1="7" x2="10" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  // Zoom out
  zoomOut: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="2"/>
    <line x1="15" y1="15" x2="21" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  // Home/center
  home: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12L12 3L21 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M5 10V20H19V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <rect x="9" y="14" width="6" height="6" fill="currentColor"/>
  </svg>`,
};

/**
 * Building Icons
 */
export const BUILDING_ICONS = {
  barracks: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="24" height="14" fill="#8b5a2b" rx="1"/>
    <path d="M4 14L16 6L28 14" fill="#c62828"/>
    <rect x="12" y="20" width="8" height="8" fill="#3d2617"/>
    <rect x="6" y="17" width="4" height="4" fill="#1a237e" opacity="0.6"/>
    <rect x="22" y="17" width="4" height="4" fill="#1a237e" opacity="0.6"/>
    <line x1="26" y1="8" x2="26" y2="14" stroke="#5d4037" stroke-width="1"/>
    <rect x="26" y="6" width="4" height="3" fill="#c62828"/>
  </svg>`,

  stable: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="14" width="28" height="14" fill="#8b5a2b" rx="1"/>
    <path d="M2 14L16 4L30 14" fill="#8b4513"/>
    <rect x="10" y="18" width="12" height="10" fill="#3d2617"/>
    <ellipse cx="22" cy="20" rx="4" ry="3" fill="#5d4037"/>
    <circle cx="23" cy="19" r="1" fill="#1a1a1a"/>
    <rect x="4" y="24" width="4" height="4" fill="#c9a66b"/>
  </svg>`,

  smithy: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="12" width="18" height="16" fill="#8b5a2b" rx="1"/>
    <rect x="4" y="10" width="18" height="4" fill="#6b4423"/>
    <rect x="22" y="8" width="8" height="20" fill="#5d5d5d"/>
    <rect x="24" y="4" width="4" height="6" fill="#607d8b"/>
    <ellipse cx="26" cy="18" rx="3" ry="2" fill="#ff5722"/>
    <ellipse cx="26" cy="18" rx="2" ry="1" fill="#ff9800"/>
    <rect x="10" y="22" width="4" height="2" fill="#424242"/>
    <rect x="8" y="24" width="8" height="4" fill="#424242"/>
  </svg>`,

  temple: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="26" rx="10" ry="3" fill="#78909c"/>
    <ellipse cx="16" cy="24" rx="8" ry="10" fill="#607d8b"/>
    <path d="M8 16L16 4L24 16" fill="#4a148c"/>
    <circle cx="16" cy="6" r="3" fill="#4fc3f7"/>
    <circle cx="16" cy="6" r="2" fill="#81d4fa"/>
    <circle cx="12" cy="4" r="1" fill="#4fc3f7" opacity="0.6"/>
    <circle cx="20" cy="5" r="0.8" fill="#4fc3f7" opacity="0.6"/>
    <rect x="14" y="20" width="4" height="6" fill="#1a237e" opacity="0.6"/>
  </svg>`,

  tower: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="28" rx="8" ry="2" fill="#5d5d5d"/>
    <path d="M10 28V10C10 10 10 6 16 6C22 6 22 10 22 10V28" fill="#6d6d6d"/>
    <rect x="8" y="6" width="3" height="4" fill="#5d5d5d"/>
    <rect x="12" y="6" width="3" height="4" fill="#5d5d5d"/>
    <rect x="17" y="6" width="3" height="4" fill="#5d5d5d"/>
    <rect x="21" y="6" width="3" height="4" fill="#5d5d5d"/>
    <path d="M10 6L16 2L22 6" fill="#c62828"/>
    <rect x="14" y="14" width="4" height="2" fill="#1a1a1a"/>
    <rect x="14" y="20" width="4" height="2" fill="#1a1a1a"/>
  </svg>`,

  mine: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 20C8 20 4 16 6 12C8 8 16 10 16 10C16 10 24 8 26 12C28 16 24 20 24 20" fill="#5d5d5d"/>
    <rect x="10" y="16" width="12" height="10" fill="#1a1a1a"/>
    <rect x="8" y="14" width="3" height="12" fill="#5d4037"/>
    <rect x="21" y="14" width="3" height="12" fill="#5d4037"/>
    <rect x="8" y="13" width="16" height="2" fill="#5d4037"/>
    <rect x="18" y="22" width="8" height="4" fill="#6d4c41"/>
    <ellipse cx="20" cy="22" rx="2" ry="1" fill="#8b7355"/>
    <ellipse cx="22" cy="22" rx="2" ry="1" fill="#a08060"/>
    <circle cx="26" cy="24" r="1.5" fill="#5d4037"/>
    <circle cx="18" cy="24" r="1.5" fill="#5d4037"/>
  </svg>`,

  farm: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="18" width="12" height="10" fill="#6d4c41"/>
    <path d="M2 18L8 12L14 18" fill="#c9a66b"/>
    <rect x="6" y="22" width="4" height="6" fill="#3e2723"/>
    <rect x="16" y="20" width="14" height="8" fill="#5d4037"/>
    <line x1="18" y1="22" x2="18" y2="26" stroke="#7cb342" stroke-width="1.5"/>
    <line x1="21" y1="21" x2="21" y2="26" stroke="#7cb342" stroke-width="1.5"/>
    <line x1="24" y1="22" x2="24" y2="26" stroke="#7cb342" stroke-width="1.5"/>
    <line x1="27" y1="21" x2="27" y2="26" stroke="#7cb342" stroke-width="1.5"/>
    <ellipse cx="18" cy="21" rx="1" ry="1.5" fill="#8bc34a"/>
    <ellipse cx="21" cy="20" rx="1" ry="1.5" fill="#8bc34a"/>
    <ellipse cx="24" cy="21" rx="1" ry="1.5" fill="#8bc34a"/>
    <ellipse cx="27" cy="20" rx="1" ry="1.5" fill="#8bc34a"/>
  </svg>`,

  market: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="24" height="14" fill="#8d6e63"/>
    <rect x="2" y="10" width="28" height="6" fill="#c62828"/>
    <rect x="4" y="20" width="8" height="2" fill="#6d4c41"/>
    <rect x="20" y="20" width="8" height="2" fill="#6d4c41"/>
    <ellipse cx="8" cy="19" rx="2" ry="1" fill="#ffd700"/>
    <ellipse cx="10" cy="19" rx="2" ry="1" fill="#e8c252"/>
    <ellipse cx="24" cy="19" rx="2" ry="1" fill="#ffd700"/>
    <circle cx="6" cy="8" r="1.5" fill="#5d4037"/>
    <circle cx="16" cy="6" r="1.5" fill="#5d4037"/>
    <circle cx="26" cy="8" r="1.5" fill="#5d4037"/>
    <line x1="6" y1="8" x2="6" y2="12" stroke="#5d4037" stroke-width="1"/>
    <line x1="16" y1="6" x2="16" y2="12" stroke="#5d4037" stroke-width="1"/>
    <line x1="26" y1="8" x2="26" y2="12" stroke="#5d4037" stroke-width="1"/>
  </svg>`,

  archery: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="24" height="14" fill="#8b5a2b" rx="1"/>
    <path d="M4 14L16 6L28 14" fill="#5d4037"/>
    <rect x="12" y="20" width="8" height="8" fill="#3d2617"/>
    <path d="M22 10C22 10 19 14 19 18C19 22 22 26 22 26" stroke="#8b4513" stroke-width="2" fill="none"/>
    <line x1="22" y1="10" x2="22" y2="26" stroke="#c4a77d" stroke-width="0.8"/>
    <circle cx="8" cy="20" r="3" fill="#5d4037" stroke="#8b4513" stroke-width="1"/>
    <circle cx="8" cy="20" r="1.5" fill="#c62828"/>
  </svg>`,

  spearcamp: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="16" width="24" height="12" fill="#8b5a2b" rx="1"/>
    <path d="M4 16L16 8L28 16" fill="#6b4423"/>
    <rect x="12" y="22" width="8" height="6" fill="#3d2617"/>
    <line x1="6" y1="4" x2="6" y2="16" stroke="#8b4513" stroke-width="1.5"/>
    <path d="M6 2L3 8H9L6 2Z" fill="#b0b0b0"/>
    <line x1="26" y1="4" x2="26" y2="16" stroke="#8b4513" stroke-width="1.5"/>
    <path d="M26 2L23 8H29L26 2Z" fill="#b0b0b0"/>
    <ellipse cx="6" cy="8" rx="1.5" ry="0.5" fill="#c62828"/>
    <ellipse cx="26" cy="8" rx="1.5" ry="0.5" fill="#c62828"/>
  </svg>`,

  wall: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="20" width="28" height="8" fill="#6a6a6a"/>
    <rect x="2" y="18" width="28" height="4" fill="#7a7a7a"/>
    <rect x="4" y="14" width="4" height="6" fill="#5a5a5a"/>
    <rect x="14" y="14" width="4" height="6" fill="#5a5a5a"/>
    <rect x="24" y="14" width="4" height="6" fill="#5a5a5a"/>
    <rect x="9" y="16" width="4" height="4" fill="#4a4a4a"/>
    <rect x="19" y="16" width="4" height="4" fill="#4a4a4a"/>
    <rect x="4" y="12" width="4" height="3" fill="#8a8a8a"/>
    <rect x="14" y="12" width="4" height="3" fill="#8a8a8a"/>
    <rect x="24" y="12" width="4" height="3" fill="#8a8a8a"/>
  </svg>`,
};

/**
 * Unit Type Icons (for unit count panel)
 */
export const UNIT_ICONS = {
  sword: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 26L14 18M14 18L26 6M14 18L10 22M26 6L28 8L26 10L24 8L26 6Z" stroke="#b0b0b0" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M6 26L8 24L10 26L8 28L6 26Z" fill="#8b4513"/>
    <line x1="11" y1="21" x2="13" y2="19" stroke="#8b4513" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  archer: ICONS.bow,

  spear: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="16" y1="6" x2="16" y2="28" stroke="#8b4513" stroke-width="2.5"/>
    <path d="M16 4L10 12H22L16 4Z" fill="#b0b0b0" stroke="#808080" stroke-width="0.5"/>
    <ellipse cx="16" cy="12" rx="3" ry="1" fill="#c62828"/>
  </svg>`,

  cavalry: ICONS.horse,

  shield: `<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 4L6 8V16C6 22 16 28 16 28C16 28 26 22 26 16V8L16 4Z" fill="#6b4423" stroke="#5c3a1e" stroke-width="1"/>
    <path d="M16 6L8 9V16C8 20.5 16 25 16 25C16 25 24 20.5 24 16V9L16 6Z" fill="#8b5a2b"/>
    <path d="M16 10L12 14L16 22L20 14L16 10Z" fill="#d4af37"/>
  </svg>`,

  taoist: ICONS.magic,
};

/**
 * Coin stack icon for costs
 */
export const COIN_STACK_SMALL = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <ellipse cx="8" cy="12" rx="6" ry="2" fill="#b8860b"/>
  <ellipse cx="8" cy="11" rx="6" ry="2" fill="#d4a017"/>
  <ellipse cx="8" cy="9" rx="6" ry="2" fill="#b8860b"/>
  <ellipse cx="8" cy="8" rx="6" ry="2" fill="#e8c252"/>
  <ellipse cx="8" cy="8" rx="4" ry="1.3" fill="#f5d547"/>
</svg>`;

/**
 * Helper to get icon HTML with size
 */
export function getIcon(iconKey: keyof typeof ICONS, size: number = 24): string {
  const icon = ICONS[iconKey];
  if (!icon) return '';
  return `<span class="ui-icon" style="width:${size}px;height:${size}px;display:inline-flex;">${icon}</span>`;
}

/**
 * Helper to get building icon HTML
 */
export function getBuildingIcon(type: string, size: number = 32): string {
  const icon = BUILDING_ICONS[type as keyof typeof BUILDING_ICONS];
  if (!icon) return '';
  return `<span class="ui-icon" style="width:${size}px;height:${size}px;display:inline-flex;">${icon}</span>`;
}

/**
 * Helper to get unit icon HTML
 */
export function getUnitIcon(type: string, size: number = 32): string {
  const icon = UNIT_ICONS[type as keyof typeof UNIT_ICONS];
  if (!icon) return '';
  return `<span class="ui-icon" style="width:${size}px;height:${size}px;display:inline-flex;">${icon}</span>`;
}

/**
 * Coin icon for cost display
 */
export function getCoinIcon(size: number = 14): string {
  return `<span class="coin-icon" style="width:${size}px;height:${size}px;display:inline-flex;">${COIN_STACK_SMALL}</span>`;
}

/**
 * UI Image Paths
 * High-quality webp images for UI elements
 */
export const UI_IMAGES = {
  // Resource icons
  icons: {
    gold: '/images/ui/icons/gold.webp',
    unit: '/images/ui/icons/unit.webp',
    time: '/images/ui/icons/time.webp',
    territory: '/images/ui/icons/territory.webp',
  },
  // Building icons
  buildings: {
    barracks: '/images/ui/buildings/barracks.webp',
    farm: '/images/ui/buildings/farm.webp',
    market: '/images/ui/buildings/market.webp',
    mine: '/images/ui/buildings/mine.webp',
    tower: '/images/ui/buildings/tower.webp',
    stable: '/images/ui/buildings/stable.webp',
    smithy: '/images/ui/buildings/smithy.webp',
    temple: '/images/ui/buildings/temple.webp',
    archery: '/images/ui/buildings/archery.webp',
    spearcamp: '/images/ui/buildings/spearcamp.webp',
    wall: '/images/ui/buildings/wall.webp',
  },
  // Unit icons
  units: {
    sword: '/images/ui/units/sword.webp',
    archer: '/images/ui/units/archer.webp',
    spear: '/images/ui/units/spear.webp',
    cavalry: '/images/ui/units/cavalry.webp',
    shield: '/images/ui/units/shield.webp',
    taoist: '/images/ui/units/taoist.webp',
  },
  // UI textures and elements
  textures: {
    wood: '/images/ui/elements/wood.webp',        // 木纹边框纹理 - 用于面板边框
    parchment: '/images/ui/elements/parchment.webp', // 羊皮纸内衬 - 用于面板内部背景
    frame: '/images/ui/elements/frame.webp',      // 金属边框装饰条 - 用于标题栏装饰
  },
  // Decorative elements
  elements: {
    shield: '/images/ui/elements/shield.webp',
  },
};

/**
 * Helper to create an image element
 */
export function getUIImage(
  category: keyof typeof UI_IMAGES,
  name: string,
  size: number = 32
): string {
  const images = UI_IMAGES[category] as Record<string, string>;
  const src = images[name];
  if (!src) return '';
  return `<img src="${src}" alt="${name}" width="${size}" height="${size}" style="object-fit:contain;" />`;
}
